import type { TransformedNumberField } from '@atlassian/jira-business-fields/src/common/field-types/number/types.tsx';
import {
	isTransformFieldSupported,
	transformField,
} from '@atlassian/jira-business-fields/src/common/transform/index.tsx';
import { PRETTY } from '@atlassian/jira-common-constants/src/jira-settings.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { IntlShape } from '@atlassian/jira-intl';
import {
	ISSUE_TYPE,
	STATUS_TYPE,
	LABELS_TYPE,
	PRIORITY_TYPE,
	URL_CF_TYPE,
	TEXT_AREA_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	TIME_ESTIMATE_TYPE,
	NUMBER_CF_TYPE,
	MULTI_USER_CF_TYPE,
	COMPONENTS_TYPE,
	LABELS_CF_TYPE,
	PEOPLE_CF_TYPE,
	PARENT_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	STORY_POINTS_TYPE,
	SPRINT_TYPE,
} from '@atlassian/jira-platform-field-config';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import type { TimeUnit } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import type { JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields as ListViewFieldsType } from '../__generated_apollo__/JWMListViewItems';
import type {
	ListViewIssueFieldsTransformed,
	ListIssueFieldTransformed,
	ListIssueIssueTypeField,
	ListIssueIssueKeyField,
	ListIssueTextField,
	ListIssueUrlCustomField,
	ListIssueTextFieldCustomField,
	ListIssueTextAreaCustomField,
	ListIssueStatusField,
	ListIssueUserField,
	ListIssueLabelsCustomField,
	ListIssueLabelsField,
	ListIssuePriorityField,
	ListIssueDateField,
	ListIssueDateTimeField,
	ListIssueSelectCustomField,
	ListIssueMultiSelectCustomField,
	ListIssueJWMCategoryCustomField,
	ListIssueTimeOriginalEstimateField,
	ListIssueDatePickerCustomField,
	ListIssueDateTimeCustomField,
	ListIssueMultiUserPickerCustomField,
	ListIssuePeopleCustomField,
	ListIssueProjectComponentsField,
	ListIssueParentField,
	TransformedUser,
	TransformedTimeOriginalEstimate,
	ProjectComponentValues,
	TransformedProjectComponentValues,
	User,
	UserNode,
	Labels,
	Sprints,
	TransformedLabels,
	TimeOriginalEstimate,
	ParentIssue,
	TransformedParentIssue,
	SelectFieldSelectedOption,
	TransformedSelectCustomFieldSelectedValue,
	SelectFieldSelectedOptionNode,
	ListIssueMultiCheckboxesCustomField,
	ListIssueSprintField,
	TransformedSprint,
	ListIssuePriorityFieldOld,
} from '../types';
import { timeFormatMap } from './constants';

const transformTimeTracking = (
	originalEstimate: TimeOriginalEstimate | null,
	timeTrackingOptions: {
		workingHoursPerDay: number;
		workingDaysPerWeek: number;
		timeFormat: string;
		defaultUnit: TimeUnit;
	},
	intl: IntlShape,
): TransformedTimeOriginalEstimate | null => {
	if (originalEstimate) {
		return {
			timeInSeconds: Number(originalEstimate.timeInSeconds) || undefined,
			timeAsString: timeTrackingFormatter(
				Number(originalEstimate.timeInSeconds),
				{
					...timeTrackingOptions,
					timeFormat: timeFormatMap.get(timeTrackingOptions.timeFormat) || PRETTY,
				},
				intl,
			),
		};
	}
	return null;
};

const transformUser = (user?: UserNode | null): TransformedUser | null => {
	if (user) {
		return {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			accountId: user.accountId!,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			name: user.name!,
			picture: user.picture ?? '',
		};
	}

	return null;
};

const transformUsers = (users: ReadonlyArray<User> | null): TransformedUser[] | null => {
	if (users) {
		return users.map((user) => transformUser(user?.node));
	}

	return null;
};

const transformSelectField = (
	selectedOption: SelectFieldSelectedOptionNode,
): TransformedSelectCustomFieldSelectedValue => {
	if (selectedOption) {
		return {
			optionId: String(selectedOption.optionId),
			value: selectedOption.value,
			...(selectedOption.color
				? {
						color: selectedOption.color?.colorKey,
					}
				: {}),
		};
	}
	return null;
};

export const transformSelectedOptions = (
	selectedOptions: ReadonlyArray<SelectFieldSelectedOption | null> | null,
): TransformedSelectCustomFieldSelectedValue[] | null => {
	if (selectedOptions) {
		return selectedOptions
			.map((option) =>
				option?.node?.optionId && option?.node?.value ? transformSelectField(option.node) : null,
			)
			.filter((option) => option !== null);
	}

	return null;
};

export const transformLabels = (labels: Labels): TransformedLabels | null => {
	if (labels) {
		return labels.map((label) => label?.node?.name ?? null).filter((label) => label !== null);
	}

	return null;
};

const transformSprint = (sprints: Sprints): TransformedSprint | null => {
	if (sprints) {
		return sprints.map((sprint) => sprint?.node ?? null).filter((sprint) => sprint !== null);
	}

	return null;
};

export const transformProjectComponents = (
	componentValues: ProjectComponentValues,
): TransformedProjectComponentValues | null => {
	if (componentValues?.length) {
		return componentValues
			.map((componentValue) =>
				componentValue?.node?.componentId
					? {
							componentId: componentValue.node?.componentId ?? '',
							name: componentValue.node?.name ?? null,
						}
					: null,
			)
			.filter((componentValue) => componentValue !== null);
	}

	return null;
};

const transformParent = (parentIssue: ParentIssue | null): TransformedParentIssue | null => {
	if (parentIssue) {
		return {
			issueId: parentIssue.issueId,
			issueKey: parentIssue.key,
		};
	}

	return null;
};

const listFieldTransformers: Record<
	string,
	(field: ListViewFieldsType, intl?: IntlShape) => ListIssueFieldTransformed | null
> = {
	JiraIssueTypeField: (field): ListIssueIssueTypeField | null => {
		if (field.__typename !== 'JiraIssueTypeField') {
			return null;
		}
		return {
			type: ISSUE_TYPE,
			fieldId: field.fieldId,
			value: field.issueType?.name ?? null,
			level: field.issueType?.hierarchy?.level,
			issueTypeId: expValEquals('list_view_conditional_formatting', 'cohort', 'variation')
				? field.issueType?.issueTypeId
				: undefined,
		};
	},
	JiraSingleLineTextField: (
		field,
	): ListIssueIssueKeyField | ListIssueTextField | ListIssueTextFieldCustomField | null => {
		if (field.__typename !== 'JiraSingleLineTextField') {
			return null;
		}
		return {
			type: field.type === 'issuekey' ? 'key' : field.type,
			fieldId: field.fieldId,
			value: field.text,
		};
	},
	JiraUrlField: (field): ListIssueUrlCustomField | null => {
		if (field.__typename !== 'JiraUrlField') {
			return null;
		}
		return {
			type: URL_CF_TYPE,
			fieldId: field.fieldId,
			value: field.uri,
		};
	},
	JiraRichTextField: (field): ListIssueTextAreaCustomField | null => {
		if (field.__typename !== 'JiraRichTextField') {
			return null;
		}
		const value = field.richText?.adfValue?.json;
		return {
			type: TEXT_AREA_CF_TYPE,
			fieldId: field.fieldId,
			value: value ? JSON.stringify(value) : '',
		};
	},
	JiraStatusField: (field): ListIssueStatusField | null => {
		if (field.__typename !== 'JiraStatusField') {
			return null;
		}
		return {
			type: STATUS_TYPE,
			fieldId: field.fieldId,
			name: field.status?.name,
			value: Number(field.status?.statusCategory?.statusCategoryId)
				? Number(field.status?.statusCategory?.statusCategoryId)
				: null,
		};
	},
	JiraSingleSelectUserPickerField: (field): ListIssueUserField | null => {
		if (field.__typename !== 'JiraSingleSelectUserPickerField') {
			return null;
		}
		return {
			type: field.type,
			fieldId: field.fieldId,
			value: transformUser(field.user),
		};
	},
	JiraLabelsField: (field): ListIssueLabelsField | ListIssueLabelsCustomField | null => {
		if (field.__typename !== 'JiraLabelsField') {
			return null;
		}
		return {
			type: field.fieldId === 'labels' ? LABELS_TYPE : LABELS_CF_TYPE,
			fieldId: field.fieldId,
			value: transformLabels(field.selectedLabelsConnection?.edges ?? null),
		};
	},
	JiraPriorityField: (field): ListIssuePriorityFieldOld | ListIssuePriorityField | null => {
		if (field.__typename !== 'JiraPriorityField') {
			return null;
		}

		if (fg('jwm_list_new_copy_paste_behaviour')) {
			return {
				type: PRIORITY_TYPE,
				fieldId: field.fieldId,
				value: field.priority
					? {
							name: field.priority.name,
							priorityId: field.priority.priorityId,
							iconUrl: field.priority.iconUrl,
						}
					: null,
			};
		}

		return {
			type: PRIORITY_TYPE,
			fieldId: field.fieldId,
			value: field.priority?.name ?? null,
			priorityId: field.priority?.priorityId ?? null,
		};
	},
	JiraDateTimePickerField: (
		field,
	): ListIssueDateTimeField | ListIssueDateTimeCustomField | null => {
		if (field.__typename !== 'JiraDateTimePickerField') {
			return null;
		}
		return {
			type: field.type,
			fieldId: field.fieldId,
			value: field.dateTime,
		};
	},
	JiraMultipleSelectField: (field): ListIssueMultiSelectCustomField | null => {
		if (field.__typename !== 'JiraMultipleSelectField') {
			return null;
		}
		return {
			type: MULTI_SELECT_CF_TYPE,
			fieldId: field.fieldId,
			value: transformSelectedOptions(field.selectedOptions?.edges ?? null),
		};
	},
	JiraSingleSelectField: (
		field,
	): ListIssueSelectCustomField | ListIssueJWMCategoryCustomField | null => {
		if (field.__typename !== 'JiraSingleSelectField') {
			return null;
		}
		return {
			type: field.type,
			fieldId: field.fieldId,
			value: transformSelectField(field.fieldOption) ?? null,
		};
	},
	// Use the timetracking field to map to the timeoriginalestimate field rather than directly using
	// timeoriginalestimate because timetracking returns data needed to determine the timeAsString.
	// Logic to request for the timetracking field data if timeoriginalestimate is in list view is in
	// `getQueryFieldIds` in src/packages/business/list/src/controllers/get-issue-fields/index.tsx
	JiraTimeTrackingField: (field, intl?: IntlShape): ListIssueTimeOriginalEstimateField | null => {
		if (
			field.__typename !== 'JiraTimeTrackingField' ||
			field.originalEstimate?.timeInSeconds == null ||
			field.timeTrackingSettings?.workingHoursPerDay == null ||
			field.timeTrackingSettings?.workingDaysPerWeek == null ||
			field.timeTrackingSettings?.defaultUnit == null ||
			intl == null
		) {
			if (fg('jwm_list_new_copy_paste_behaviour')) {
				return {
					type: TIME_ESTIMATE_TYPE,
					fieldId: 'timeoriginalestimate',
					value: null,
				};
			}

			return null;
		}
		const {
			workingHoursPerDay,
			workingDaysPerWeek,
			defaultFormat: timeFormat,
			defaultUnit,
		} = field.timeTrackingSettings;

		return {
			type: TIME_ESTIMATE_TYPE,
			fieldId: 'timeoriginalestimate',
			value: transformTimeTracking(
				field.originalEstimate,
				{
					workingHoursPerDay,
					workingDaysPerWeek,
					timeFormat: timeFormat || PRETTY,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					defaultUnit: defaultUnit as unknown as TimeUnit,
				},
				intl,
			),
		};
	},
	JiraDatePickerField: (field): ListIssueDateField | ListIssueDatePickerCustomField | null => {
		if (field.__typename !== 'JiraDatePickerField') {
			return null;
		}
		return {
			type: field.type,
			fieldId: field.fieldId,
			value: field.date,
		};
	},
	JiraNumberField: (field): TransformedNumberField | null => {
		if (field.__typename !== 'JiraNumberField') {
			return null;
		}
		return {
			type:
				field.type === STORY_POINT_ESTIMATE_CF_TYPE || field.type === STORY_POINTS_TYPE
					? field.type
					: NUMBER_CF_TYPE,
			fieldId: field.fieldId,
			value: field.number,
		};
	},
	JiraMultipleSelectUserPickerField: (field): ListIssueMultiUserPickerCustomField | null => {
		if (field.__typename !== 'JiraMultipleSelectUserPickerField') {
			return null;
		}
		return {
			type: MULTI_USER_CF_TYPE,
			fieldId: field.fieldId,
			value: transformUsers(field.selectedUsersConnection?.edges ?? null),
		};
	},
	JiraPeopleField: (field): ListIssuePeopleCustomField | null => {
		if (field.__typename !== 'JiraPeopleField') {
			return null;
		}
		return {
			type: PEOPLE_CF_TYPE,
			fieldId: field.fieldId,
			value: transformUsers(field.selectedUsersConnection?.edges ?? null),
		};
	},
	JiraComponentsField: (field): ListIssueProjectComponentsField | null => {
		if (field.__typename !== 'JiraComponentsField') {
			return null;
		}
		return {
			type: COMPONENTS_TYPE,
			fieldId: field.fieldId,
			value: transformProjectComponents(field.selectedComponentsConnection?.edges ?? null),
		};
	},
	JiraParentIssueField: (field): ListIssueParentField | null => {
		if (field.__typename !== 'JiraParentIssueField') {
			return null;
		}
		return {
			type: PARENT_TYPE,
			fieldId: field.fieldId,
			value: transformParent(field.parentIssue),
		};
	},
	JiraCheckboxesField: (field): ListIssueMultiCheckboxesCustomField | null => {
		if (field.__typename !== 'JiraCheckboxesField') {
			return null;
		}
		return {
			type: MULTI_CHECKBOXES_CF_TYPE,
			fieldId: field.fieldId,
			value: transformSelectedOptions(field.selectedOptions?.edges ?? null),
		};
	},
	JiraSprintField: (field): ListIssueSprintField | null => {
		if (field.__typename !== 'JiraSprintField') {
			return null;
		}
		return {
			type: SPRINT_TYPE,
			fieldId: field.fieldId,
			value: transformSprint(field.selectedSprintsConnection?.edges ?? null),
		};
	},
};

const listViewTransformField = (field: ListViewFieldsType | null, intl: IntlShape) => {
	if (field == null) {
		return null;
	}
	if (isTransformFieldSupported(field) && fg('jsw_list_view_-_all_the_fields')) {
		return transformField(field, intl);
	}
	if (field.__typename in listFieldTransformers) {
		return listFieldTransformers[field.__typename](field, intl);
	}

	return null;
};

export const listViewTransformFields = (
	fields: ReadonlyArray<ListViewFieldsType | null> | null,
	intl: IntlShape,
): ListViewIssueFieldsTransformed => {
	const toReturn: ListViewIssueFieldsTransformed = [];
	fields?.forEach((field) => {
		const transformedField = listViewTransformField(field, intl);
		if (transformedField != null) {
			toReturn.push(transformedField);
		}
	});

	return toReturn;
};
